import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
// Import the login thunk
import { login_thunk, forgot_password_thunk } from "../redux/auth/authThunk";
import { setIsLogin } from "../redux/auth/authSlice";
import { setSuccessAlert } from "../redux/alert/alertSlice";
import { USER_AU_TOKEN, USER_AU_USER } from "../common/key";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleForgotPasswordEmailChange = (e) => {
    setForgotPasswordEmail(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForgotPasswordEmail = () => {
    const newErrors = {};
    if (!forgotPasswordEmail.trim()) {
      newErrors.forgotPasswordEmail = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(forgotPasswordEmail)) {
      newErrors.forgotPasswordEmail = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);

      // Dispatch the thunk and get the resulting action
      const resultAction = await dispatch(login_thunk(formData));

      // Check if the action was fulfilled or rejected
      if (login_thunk.fulfilled.match(resultAction)) {
        if (resultAction?.payload?.data?.user?.isVerified) {
          // dispatch(setIsLogin(true));
          // dispatch(setSuccessAlert("Login successful"));
          // navigate("/");
        } else {
          // Success case - redirect to code page after successful login
          navigate("/code", { state: { email: formData.email } });
        }
      } else {
        // Error case - get the error message from the payload
        const errorMessage = resultAction.payload || "Login failed";
        console.error("Login failed:", errorMessage);

        // Handle different types of errors
        if (errorMessage.includes("Invalid email or password")) {
          setErrors({
            ...errors,
            general: "Invalid email or password",
          });
        } else if (errorMessage.includes("Account not verified")) {
          // If account is not verified, navigate to verification page
          navigate("/code", { state: { email: formData.email } });
        } else {
          setErrors({
            ...errors,
            general: errorMessage,
          });
        }
      }

      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (validateForgotPasswordEmail()) {
      setForgotPasswordLoading(true);

      // Dispatch the forgot password thunk
      const resultAction = await dispatch(
        forgot_password_thunk({ email: forgotPasswordEmail })
      );

      // Check if the action was fulfilled or rejected
      if (forgot_password_thunk.fulfilled.match(resultAction)) {
        // Reset the form and go back to login
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      } else {
        // Error handled by the thunk through the error alert
        console.error("Failed to send reset email");
      }

      setForgotPasswordLoading(false);
    }
  };

  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
        </div>
        <h2 className="text-center text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500">
          {showForgotPassword ? "Reset Password" : "Welcome Back"}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400">
          {showForgotPassword ? (
            <>
              Remember your password?{" "}
              <button
                onClick={toggleForgotPassword}
                className="font-medium text-purple-500 hover:text-purple-400 transition-colors"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{" "}
              <Link
                to="/register"
                className="font-medium text-purple-500 hover:text-purple-400 transition-colors"
              >
                Register
              </Link>
            </>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-[#0F172A] py-8 px-4 shadow-xl shadow-black/20 sm:rounded-xl sm:px-10 border border-gray-800">
          {showForgotPassword ? (
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              {errors.general && (
                <div className="bg-red-900/50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-400">{errors.general}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="forgotPasswordEmail"
                  className="block text-sm font-medium text-gray-300"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="forgotPasswordEmail"
                    name="forgotPasswordEmail"
                    type="email"
                    autoComplete="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={handleForgotPasswordEmailChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.forgotPasswordEmail
                        ? "border-red-500"
                        : "border-gray-700"
                    } bg-gray-800/50 rounded-lg shadow-sm placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 
                    text-white transition-colors sm:text-sm`}
                  />
                  {errors.forgotPasswordEmail && (
                    <p className="mt-2 text-sm text-red-500">
                      {errors.forgotPasswordEmail}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white 
                  bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                  transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {forgotPasswordLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {errors.general && (
                <div className="bg-red-900/50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-400">{errors.general}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-300"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.email ? "border-red-500" : "border-gray-700"
                    } bg-gray-800/50 rounded-lg shadow-sm placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 
                    text-white transition-colors sm:text-sm`}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-300"
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={toggleForgotPassword}
                    className="text-sm font-medium text-purple-500 hover:text-purple-400 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.password ? "border-red-500" : "border-gray-700"
                    } bg-gray-800/50 rounded-lg shadow-sm placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 
                    text-white transition-colors sm:text-sm`}
                  />
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-500">
                      {errors.password}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white 
                  bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                  transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Signing in...
                    </div>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
